import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateTireInspectionList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/tire-inspections/search');
};

export const useTireInspectionList = (
  page,
  rowsPerPage,
  searchKey,
  searchValue,
  selectedCompanies = [],
  extraFilters = [],
  enabled = true
) => {
  const isFetchAll = page === undefined;
  const start = isFetchAll ? 0 : page * rowsPerPage;
  const companies = (selectedCompanies || []).map((c) => c.value ?? c).join(',');

  const payload = useMemo(() => {
    if (!enabled || isFetchAll) return null;
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .addJoin('company', ['name'])
      .addJoin('vehicle', ['license_plate'])
      .addJoin('tire_item', [
        'name',
        'tire_brand_name',
        'tire_size_code',
        'tire_spec_name',
        'status',
        'total_price',
        'receive_date',
        'supplier',
        'batch_number',
        'warehouse_name'
      ])
      .setOrder(['inspection_date'], false);

    if (companies) {
      if (companies.includes(',')) {
        fb.in('company_id', companies);
      } else {
        fb.equal('company_id', companies);
      }
    } else {
      fb.filterByCompany();
    }

    if (searchKey && searchValue) {
      const exactFields = ['serial_number', 'condition', 'tire_item_id'];
      if (exactFields.includes(searchKey)) {
        fb.equal(searchKey, searchValue);
      } else {
        fb.like(searchKey, searchValue, 'AND');
      }
    }

    if (extraFilters && extraFilters.length > 0) {
      extraFilters.forEach((f) => {
        if (f.operator && typeof fb[f.operator] === 'function') {
          if (f.operator === 'range') {
            fb.range(f.field, f.value, f.value1, f.logic || 'AND', f.tableName || null);
          } else {
            fb[f.operator](f.field, f.value, f.logic || 'AND', f.tableName || null);
          }
        } else {
          fb.equal(f.field, f.value);
        }
      });
    }

    return fb.build();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isFetchAll, start, rowsPerPage, searchKey, searchValue, companies, JSON.stringify(extraFilters)]);

  const {
    data,
    error,
    isLoading,
    mutate: swrMutate
  } = useSWR(payload ? ['/tire-inspections/search', payload] : null, fetcher, {
    keepPreviousData: true
  });

  return {
    data: Array.isArray(data?.data) ? data.data : [],
    totalRecord: data?.totalRecord || data?.dataSize || 0,
    isLoading,
    error,
    mutate: swrMutate,
    refetch: swrMutate
  };
};

export const createTireInspection = async (payload) => {
  try {
    const response = await fleetApi.post(`/tire-inspections/new${getTzParam()}`, payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireInspectionList();
    return response.data;
  } catch (error) {
    console.error('Error creating tire inspection:', error);
    throw error;
  }
};

export const updateTireInspection = async (id, payload) => {
  try {
    const response = await fleetApi.put(`/tire-inspections/edit/${id}${getTzParam()}`, payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireInspectionList();
    return response.data;
  } catch (error) {
    console.error('Error updating tire inspection:', error);
    throw error;
  }
};
